import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  MenuItem,
  Paper,
  Button,
  Select,
  InputLabel,
  FormControl
} from "@mui/material";
import DetailsPageLowerLayer from '../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer';
import TopSection from '../../../CommonComponents/TopSection';
import { fetchUserRequestData, fetchApproverRequestStatuses, updateApproverAction } from '../../../services/UserRole/RequestStatusService';

const formatDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};
const INPUT_WIDTH = 450;

const TrackAccessRequest = () => {
  const breadcrumbArray = ["User Access Management", "Request Status"];
  const [toolbarStatus, setToolbarStatus] = useState("");
  const [userRequestRows, setUserRequestRows] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [approverData, setApproverData] = useState([]);
  const [loadingApprover, setLoadingApprover] = useState(false);

  const handleApprove = async (row) => {
    try {
      const payload = {
        accessRequestId: row.accessRequestId,
        requestId: row.requestId,
        action: "APPROVE"
      };
      await updateApproverAction(payload);

      setApproverData(prev =>                                        // Remove row from table       
        prev.filter(item => item.accessRequestId !== row.accessRequestId)
      );
    } catch (error) {
      console.error("Approve failed", error);
    }
  };

  const handleReject = async (row) => {
    try {
      const payload = {
        accessRequestId: row.accessRequestId,
        requestId: row.requestId,
        action: "REJECT"
      };
      await updateApproverAction(payload);

      setApproverData(prev =>                                            // Remove row from table
        prev.filter(item => item.accessRequestId !== row.accessRequestId)
      );
    } catch (error) {
      console.error("Reject failed", error);
    }
  };

  const approvalStatusOptions = useMemo(() => {
    return [...new Set(userRequestRows.map(r => r.approvalStatus))];
  }, [userRequestRows]);

  useEffect(() => {
    const fetchUserRequests = async () => {
      try {
        setLoadingUser(true);
        const userRequestData = await fetchUserRequestData();
        const mappedData = userRequestData.userRequestStatuses.map((item) => ({
          requestId: item.requestId,
          requestedUser: item.requestedUser,
          approvalWorkflow: item.approvalWorkflow,
          workflowType: item.approvalWorkflowType,
          workflowApprover: item.workflowApprover,
          approvalStatus: item.approvalStatus,
          referenceId: item.referenceId,
          lastUpdatedOn: item.modifiedDatetime
        }));
        setUserRequestRows(mappedData);
      } catch (error) {
        console.error("Failed to fetch request status", error);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUserRequests();
  }, []);

  useEffect(() => {
    const fetchApproverRequests = async () => {
      try {
        setLoadingApprover(true);
        const response = await fetchApproverRequestStatuses();

        const mappedData = response.approverRequestStatuses
          .filter(item => !["APPROVED", "REJECTED"].includes((item.approvalStatus || "").toUpperCase()))
          .filter(item => (item.approvalWorkflowType || "").toUpperCase() !== "AD-TICKET")
          .map(item => ({
            accessRequestId: item.accessRequestId,
            requestId: item.requestId,
            user: item.requestedUser,
            approvalWorkflow: item.approvalWorkflow,
            workflowType: item.approvalWorkflowType,
            workflowApprover: item.workflowApprover,
            status: item.approvalStatus,
            referenceId: item.referenceId
          }));
        setApproverData(mappedData);
      } catch (error) {
        console.error("Failed to fetch approver requests", error);
      } finally {
        setLoadingApprover(false);
      }
    };
    fetchApproverRequests();
  }, []);

  const userTab = useMemo(() => ({
    label: `User`,
    loading: loadingUser,
    columns: [
      { field: 'requestId', headerName: 'Request ID', flex: 1 },
      { field: 'requestedUser', headerName: 'Requested User', flex: 1 },
      { field: 'approvalWorkflow', headerName: 'Approval Workflow', flex: 1 },
      { field: 'workflowType', headerName: 'Approval Workflow Type', flex: 1 },
      { field: 'workflowApprover', headerName: 'Workflow Approver', flex: 1 },
      { field: 'approvalStatus', headerName: 'Approval Status', flex: 1 },
      { field: 'referenceId', headerName: 'Reference ID', flex: 1 },
      { field: 'lastUpdatedOn', headerName: 'Last Updated On', flex: 1 },
    ],
    rows: toolbarStatus
      ? userRequestRows.filter(r => r.approvalStatus === toolbarStatus) : userRequestRows,
    dropdowns: [
      {
        label: "Approval Status",
        options: approvalStatusOptions,
        onChange: setToolbarStatus,
      },
    ],
  }), [toolbarStatus, userRequestRows, approvalStatusOptions, loadingUser]);

  const approverTab = useMemo(
    () => ({
      label: "Approver",
      loading: loadingApprover,
      columns: [
        { field: "requestId", headerName: "Request ID", flex: 1 },
        { field: "user", headerName: "Requested User", flex: 1 },
        { field: 'approvalWorkflow', headerName: 'Approval Workflow', flex: 1 },
        { field: 'workflowType', headerName: 'Approval Workflow Type', flex: 1 },
        { field: 'workflowApprover', headerName: 'Workflow Approver', flex: 1 },
        { field: "status", headerName: "Status", flex: 1 },
        { field: 'referenceId', headerName: 'Reference ID', flex: 1 },
        {
          field: "action",
          headerName: "Action",
          flex: 1,
          cellRenderer: (params) => (
            <Box display="flex" mt={0.5} gap={1}>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={params.data.status === "APPROVED" || params.data.status === "REJECTED"}
                onClick={() => handleApprove(params.data)}
              >
                Approve
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={params.data.status === "APPROVED" || params.data.status === "REJECTED"}
                onClick={() => handleReject(params.data)}
              >
                Reject
              </Button>
            </Box>
          ),
        },
      ],
      rows: approverData,
    }),
    [approverData, loadingApprover]
  );

  const tabs = [userTab, approverTab];

  return (
    <div>
      <TopSection breadcrumbArray={breadcrumbArray} hideHeader={true} headerName={""} hideTitle={true} />
      <Paper elevation={2} sx={{ mt: 0.5, mb: 3, pt: 1, pb: 1.5, bgcolor: '#ddd', height: 60 }}>
        <Box mt={2} mb={3} mx="auto" sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          width: INPUT_WIDTH
        }}>
          <FormControl size="small">
            <InputLabel id="application-label">Application</InputLabel>
            <Select
              labelId="application-label"
              id="application"
              value="Command Center"
              label="Application"
              disabled
              sx={{ color: "#000" }}
            >
              <MenuItem value="Command Center">
                Command Center
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      <DetailsPageLowerLayer tabs={tabs} />
    </div>
  );
};

export default TrackAccessRequest;